import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, User, ChevronDown, ShieldCheck, Users, HeartHandshake, CalendarDays, ArrowRight,
  Phone, Mail, Clock, FileText, CheckCircle2, X, Plus, MapPin, ClipboardList, MessageCircle, Award
} from 'lucide-react';

const NAVY = '#1B2380', TEAL = '#0E8C84', BG = '#F5F7FB', PANEL = '#EEF1FA';
const logoUrl = 'https://customer-assets.emergentagent.com/job_consulenza-terzo/artifacts/ldtrfvrt_Assietica%20Soc.Coop.%20%282%29.png';

const SETTORI = {
  coop: { c: '#5E9732', n: 'Cooperative sociali' },
  odv: { c: '#F39200', n: 'ODV e APS' },
  eventi: { c: '#E53935', n: 'Eventi' },
  famiglie: { c: '#2A62A0', n: 'Persone e famiglie' },
};

const Btn = ({ children, primary, className = '' }) => (
  <button className={`${primary ? 'text-white' : 'text-white'} font-bold uppercase tracking-wide rounded-lg px-5 py-3 transition-colors ${className}`}
    style={{ fontSize: 12, backgroundColor: primary ? TEAL : NAVY }}>{children}</button>
);

const Header = () => (
  <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
    <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center gap-4 lg:gap-6 h-[72px]">
      <img src={logoUrl} alt="Assietica" style={{ height: 44 }} />
      <nav className="hidden lg:flex items-center gap-5 text-sm font-medium" style={{ color: NAVY, fontFamily: 'Work Sans' }}>
        <span className="flex items-center gap-1 cursor-pointer">Per chi <ChevronDown size={14} /></span>
        <span className="flex items-center gap-1 cursor-pointer">Esigenze <ChevronDown size={14} /></span>
        <span className="cursor-pointer">Chi siamo</span>
      </nav>
      <div className="hidden md:flex flex-1 max-w-xs items-center gap-2 rounded-full px-4 py-2" style={{ backgroundColor: PANEL }}>
        <Search size={15} className="text-gray-400" />
        <span className="text-sm text-gray-400" style={{ fontFamily: 'Work Sans' }}>Cerca una soluzione…</span>
      </div>
      <div className="ml-auto flex items-center gap-2.5">
        <button className="hidden sm:flex items-center gap-1.5 font-bold uppercase rounded-lg px-4 py-3 text-white" style={{ fontSize: 12, backgroundColor: NAVY }}>
          <User size={14} /> Area clienti
        </button>
        <Btn primary>Richiedi il Check-up di Protezione</Btn>
      </div>
    </div>
    {/* Mega menu aperto (mockup) */}
    <div className="hidden xl:block border-t border-gray-100 bg-white">
      <div className="max-w-7xl mx-auto px-8 py-3 flex gap-6 text-xs" style={{ fontFamily: 'Work Sans' }}>
        {['Cooperative sociali', 'ODV', 'APS', 'Fondazioni', 'Imprese sociali', 'Persone e famiglie'].map((v) => (
          <span key={v} className="text-gray-600 hover:underline cursor-pointer">{v}</span>
        ))}
        <span className="text-gray-300">|</span>
        {['Responsabilità civile', 'Volontari', 'Amministratori', 'Beni e sedi', 'Eventi', 'Rischi catastrofali'].map((v) => (
          <span key={v} className="text-gray-600 hover:underline cursor-pointer">{v}</span>
        ))}
      </div>
    </div>
  </header>
);

const Footer = () => (
  <footer className="text-white mt-16" style={{ backgroundColor: NAVY }}>
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid md:grid-cols-3 gap-8 text-sm" style={{ fontFamily: 'Work Sans' }}>
      <div>
        <p className="font-semibold mb-2" style={{ fontFamily: 'Outfit' }}>Assietica Soc. Coop. Società Benefit</p>
        <p className="text-white/70 leading-relaxed flex items-start gap-2"><MapPin size={14} className="mt-0.5 flex-shrink-0" /> Viale Antonio Gramsci 8/G — 08015 Macomer (NU)<br />P.IVA / C.F. 01435480916</p>
      </div>
      <div className="text-white/70 leading-relaxed">
        <p>Intermediario iscritto al RUI n. A000480096 (Sez. A)</p>
        <p>Attività soggetta alla vigilanza IVASS — Mandato Assimoco</p>
        <p className="underline cursor-pointer">Procedura reclami · Documenti e trasparenza</p>
      </div>
      <div className="text-white/70 leading-relaxed">
        <p className="flex items-center gap-2"><Phone size={14} /> 0785 747193</p>
        <p className="flex items-center gap-2"><Mail size={14} /> agenzia@assietica.it · PEC cdsp@pec.confcooperative.it</p>
        <p className="flex items-center gap-2"><Clock size={14} /> Lun-Ven 9:00-13:00 / 15:30-18:00</p>
      </div>
    </div>
  </footer>
);

const SolCard = ({ settore, nome, per, tags }) => {
  const s = SETTORI[settore];
  return (
    <div className="bg-white rounded-2xl p-6 transition-shadow hover:shadow-lg cursor-pointer border border-gray-100">
      <span className="inline-block rounded-full px-3 py-1 text-[11px] font-bold text-white mb-3" style={{ backgroundColor: s.c }}>{s.n}</span>
      <h3 className="text-lg mb-1" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>{nome}</h3>
      <p className="text-sm text-gray-500 mb-3" style={{ fontFamily: 'Work Sans' }}>Per: {per}</p>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {tags.map((t) => <span key={t} className="rounded-full px-2.5 py-0.5 text-[11px]" style={{ backgroundColor: PANEL, color: NAVY }}>{t}</span>)}
      </div>
      <span className="text-sm font-semibold flex items-center gap-1" style={{ color: TEAL }}>Scopri e aggiungi alla richiesta <ArrowRight size={14} /></span>
    </div>
  );
};

const HomeView = () => (
  <>
    <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-12 pb-10 grid lg:grid-cols-2 gap-8 items-center">
      <div>
        <h1 className="text-4xl lg:text-5xl leading-tight mb-4" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>
          Ti aiutiamo a capire se il tuo ente è protetto davvero.
        </h1>
        <p className="text-gray-600 mb-6 text-lg" style={{ fontFamily: 'Work Sans' }}>
          Il Check-up di Protezione è gratuito e senza impegno: riceverai un riepilogo scritto delle tue coperture e dei punti da migliorare. Ti risponde una persona entro 24 ore lavorative.
        </p>
        <div className="flex flex-wrap gap-3">
          <Btn primary>Richiedi il Check-up di Protezione</Btn>
          <Btn>Parla con un consulente</Btn>
        </div>
      </div>
      <div className="rounded-2xl p-8 grid grid-cols-2 gap-4" style={{ backgroundColor: PANEL }}>
        {[{ i: ShieldCheck, t: 'Sono una cooperativa', s: 'coop' }, { i: HeartHandshake, t: "Sono un'associazione", s: 'odv' },
          { i: Users, t: 'Ho volontari', s: 'famiglie' }, { i: CalendarDays, t: 'Organizzo eventi', s: 'eventi' }].map(({ i: I, t, s }) => (
          <div key={t} className="bg-white rounded-2xl p-5 hover:shadow-lg transition-shadow cursor-pointer">
            <I size={26} style={{ color: SETTORI[s].c }} />
            <p className="mt-3 font-semibold text-sm" style={{ fontFamily: 'Outfit', color: NAVY }}>{t}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <h2 className="text-2xl mb-5" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Soluzioni in evidenza</h2>
      <div className="grid md:grid-cols-3 gap-5">
        <SolCard settore="coop" nome="Protezione RC per cooperative sociali" per="cooperative di tipo A, B e miste" tags={['Utenti come terzi', 'RCO', 'Convenzioni ASL']} />
        <SolCard settore="odv" nome="Volontari protetti (art. 18 CTS)" per="APS, ODV e fondazioni iscritte al RUNTS" tags={['Infortuni', 'Malattia', 'RC verso terzi']} />
        <SolCard settore="eventi" nome="Eventi e manifestazioni" per="chi organizza eventi aperti al pubblico" tags={['Espositori', 'Strutture temporanee', 'Alimenti']} />
      </div>
    </section>

    <section style={{ backgroundColor: PANEL }} className="py-10">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <h2 className="text-2xl mb-6" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Come funziona</h2>
        <div className="grid md:grid-cols-4 gap-4">
          {[['1. Raccontaci il tuo ente', 'Tre passi brevi, solo le domande necessarie', ClipboardList],
            ['2. Analizziamo insieme', 'Un consulente esamina coperture e punti scoperti', Search],
            ['3. Ricevi il riepilogo scritto', 'Chiaro, senza punteggi né allarmismi', FileText],
            ['4. Decidi tu', 'Nessun obbligo: la consulenza è gratuita', CheckCircle2]].map(([t, d, I]) => (
            <div key={t} className="bg-white rounded-2xl p-5">
              <I size={22} style={{ color: TEAL }} />
              <p className="mt-3 font-semibold text-sm" style={{ fontFamily: 'Outfit', color: NAVY }}>{t}</p>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Work Sans' }}>{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-2 gap-8 items-start">
      <div>
        <span className="inline-block rounded-full px-3 py-1 text-[11px] font-bold text-white mb-3" style={{ backgroundColor: TEAL }}>Società Cooperativa — Società Benefit</span>
        <h2 className="text-2xl lg:text-3xl mb-4" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Chi è Assietica</h2>
        <div className="space-y-3 text-sm lg:text-[15px] text-gray-600 leading-relaxed" style={{ fontFamily: 'Work Sans' }}>
          <p>Assietica è una <b style={{ color: NAVY }}>Società Cooperativa e Società Benefit</b>. Abbiamo scelto la cooperazione perché crediamo nella <b style={{ color: NAVY }}>forza dell'unione e della mutualità</b>: il modello più democratico per gestire un servizio delicato come quello assicurativo. In cooperativa <b style={{ color: NAVY }}>la persona viene prima del capitale</b>.</p>
          <p>Nasciamo da oltre <b style={{ color: NAVY }}>35 anni di volontariato attivo</b> dei nostri fondatori — soccorso sanitario, protezione civile, sostegno alle persone con disabilità. È lì che abbiamo imparato ad ascoltare prima di proporre.</p>
          <p><b style={{ color: TEAL }}>La nostra dichiarazione per il Terzo Settore:</b> proteggere chi genera impatto positivo, con consulenza chiara e senza pressioni. Nessuna vendita a distanza: decidi tu, sempre, con un consulente al tuo fianco.</p>
        </div>
      </div>
      <div className="grid gap-4">
        {[[HeartHandshake, 'Mutualità e cooperazione', 'Un socio, un voto: le decisioni si prendono insieme, non in base al capitale.'],
          [Users, 'La persona prima del capitale', 'Gli utili rafforzano la cooperativa e il territorio, non la rendita.'],
          [Award, '35 anni di volontariato attivo', "L'impegno per il bene comune è scritto nel nostro statuto di Società Benefit."]].map(([I, t, d]) => (
          <div key={t} className="bg-white border border-gray-100 rounded-2xl p-5 flex items-start gap-4 hover:shadow-lg transition-shadow">
            <div className="rounded-2xl p-3 flex-shrink-0" style={{ backgroundColor: PANEL }}><I size={22} style={{ color: TEAL }} /></div>
            <div>
              <p className="font-semibold text-sm" style={{ fontFamily: 'Outfit', color: NAVY }}>{t}</p>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Work Sans' }}>{d}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid md:grid-cols-2 gap-8 items-center">
      <div className="grid grid-cols-3 gap-4">
        {[['128', 'richieste gestite'], ['46', 'enti seguiti'], ['320', 'ore donate al territorio']].map(([n, l]) => (
          <div key={l} className="rounded-2xl p-5 text-center" style={{ backgroundColor: PANEL }}>
            <p className="text-3xl" style={{ fontFamily: 'Outfit', fontWeight: 600, color: TEAL }}>{n}</p>
            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Work Sans' }}>{l}<br /><span className="text-gray-400">dal 21/02/2024</span></p>
          </div>
        ))}
      </div>
      <div>
        <h2 className="text-2xl mb-3" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Chi ti risponde</h2>
        <div className="flex gap-4">
          {['Mauro', 'Umberto'].map((n) => (
            <div key={n} className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: NAVY, fontFamily: 'Outfit' }}>{n[0]}</div>
              <div><p className="font-semibold text-sm" style={{ fontFamily: 'Outfit', color: NAVY }}>{n}</p>
                <p className="text-xs text-gray-500" style={{ fontFamily: 'Work Sans' }}>Consulente — foto reale</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

const SoluzioneView = () => (
  <section className="max-w-4xl mx-auto px-4 lg:px-8 py-10">
    <span className="inline-block rounded-full px-3 py-1 text-[11px] font-bold text-white mb-3" style={{ backgroundColor: SETTORI.odv.c }}>ODV e APS</span>
    <h1 className="text-3xl lg:text-4xl mb-2" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Volontari protetti (art. 18 CTS)</h1>
    <p className="text-gray-500 mb-4" style={{ fontFamily: 'Work Sans' }}>Per: APS, ODV e fondazioni iscritte al RUNTS</p>
    <div className="flex gap-1.5 mb-6">{['Infortuni', 'Malattia', 'RC verso terzi'].map((t) => <span key={t} className="rounded-full px-3 py-1 text-xs" style={{ backgroundColor: PANEL, color: NAVY }}>{t}</span>)}</div>
    <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-5" style={{ fontFamily: 'Work Sans' }}>
      <p className="text-gray-700 leading-relaxed text-sm">Gli enti del Terzo Settore devono assicurare i volontari per infortuni, malattie e responsabilità civile. Ti aiutiamo a capire se la copertura in essere è adatta al tuo ente e ai tuoi volontari — senza prezzi online: il confronto avviene sempre con un consulente.</p>
      <p className="text-gray-700 text-sm mt-3"><b style={{ color: NAVY }}>Un esempio:</b> cosa succede se un volontario si fa male durante un evento? Ne parliamo insieme, con calma.</p>
    </div>
    <div className="bg-white border border-gray-100 rounded-2xl p-6 mb-6" style={{ fontFamily: 'Work Sans' }}>
      <p className="font-semibold text-sm mb-3" style={{ fontFamily: 'Outfit', color: NAVY }}>Domande frequenti</p>
      {['Il registro dei volontari è obbligatorio?', 'I soci volontari di una cooperativa sono diversi?'].map((q) => (
        <p key={q} className="text-sm text-gray-600 py-2 border-t border-gray-100 flex justify-between items-center">{q} <ChevronDown size={14} /></p>
      ))}
    </div>
    <div className="flex gap-3"><Btn primary><Plus size={13} className="inline mr-1 -mt-0.5" />Aggiungi alla tua richiesta</Btn><Btn>Parla con un consulente</Btn></div>
  </section>
);

const CheckupView = () => (
  <section className="max-w-2xl mx-auto px-4 py-10">
    <h1 className="text-3xl mb-1" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>Check-up di Protezione</h1>
    <p className="text-gray-500 text-sm mb-6" style={{ fontFamily: 'Work Sans' }}>Gratuito e senza impegno. Riceverai un riepilogo scritto delle tue coperture e dei punti da migliorare.</p>
    <div className="mb-6">
      <div className="flex justify-between text-xs mb-2" style={{ fontFamily: 'Work Sans', color: NAVY }}><b>Passo 1 di 3 — Il tuo ente</b><span className="text-gray-400">2 minuti</span></div>
      <div className="h-2 rounded-full" style={{ backgroundColor: PANEL }}><div className="h-full rounded-full" style={{ width: '33%', backgroundColor: TEAL }} /></div>
    </div>
    <div className="bg-white border border-gray-100 rounded-2xl p-6 space-y-5" style={{ fontFamily: 'Work Sans' }}>
      <div>
        <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Nome dell'ente *</label>
        <input className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" placeholder="Es. Cooperativa Il Girasole" readOnly />
      </div>
      <div>
        <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Che tipo di ente siete? *</label>
        <div className="flex flex-wrap gap-2">
          {['Cooperativa sociale', 'APS', 'ODV', 'Fondazione', 'Altro'].map((o, i) => (
            <span key={o} className={`rounded-full px-4 py-2 text-sm border-2 cursor-pointer ${i === 1 ? 'text-white' : 'text-gray-600 border-gray-200'}`}
              style={i === 1 ? { backgroundColor: NAVY, borderColor: NAVY } : {}}>{o}</span>
          ))}
        </div>
      </div>
      <div>
        <label className="text-sm font-semibold block mb-1.5" style={{ color: NAVY }}>Avete volontari?</label>
        <p className="text-xs text-gray-400 mb-2">Ci serve per capire quante persone proteggere.</p>
        <div className="flex gap-2">{['Sì', 'No', 'Non so'].map((o) => <span key={o} className="rounded-full px-4 py-2 text-sm border-2 border-gray-200 text-gray-600 cursor-pointer">{o}</span>)}</div>
      </div>
      <div className="border-t border-gray-100 pt-4 space-y-2.5 text-xs text-gray-600">
        <label className="flex items-start gap-2"><input type="checkbox" readOnly className="mt-0.5" /> Ho preso visione dell'<u>Informativa Privacy</u> e dell'<u>Informativa sul distributore (MUP)</u>. *</label>
        <label className="flex items-start gap-2"><input type="checkbox" readOnly className="mt-0.5" /> Acconsento a essere ricontattato con mezzi elettronici (email, telefono, WhatsApp) per rispondere alla richiesta.</label>
        <label className="flex items-start gap-2"><input type="checkbox" readOnly className="mt-0.5" /> Desidero ricevere la newsletter (facoltativo, non serve per avere il Check-up).</label>
      </div>
      <Btn primary className="w-full">Continua — Passo 2 di 3</Btn>
    </div>
  </section>
);

const RichiestaView = () => (
  <div className="relative min-h-[600px]">
    <HomeView />
    <div className="fixed inset-0 bg-black/40 z-40" />
    <aside className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl p-6 flex flex-col" style={{ fontFamily: 'Work Sans' }}>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl" style={{ fontFamily: 'Outfit', fontWeight: 600, color: NAVY }}>La tua richiesta</h2>
        <X size={20} className="text-gray-400" />
      </div>
      {[['Volontari protetti (art. 18 CTS)', 'odv'], ['Eventi e manifestazioni', 'eventi']].map(([n, s]) => (
        <div key={n} className="rounded-2xl p-4 mb-3 flex items-center justify-between" style={{ backgroundColor: PANEL }}>
          <div><span className="text-[10px] font-bold text-white rounded-full px-2 py-0.5" style={{ backgroundColor: SETTORI[s].c }}>{SETTORI[s].n}</span>
            <p className="text-sm font-semibold mt-1.5" style={{ color: NAVY }}>{n}</p></div>
          <X size={16} className="text-gray-400" />
        </div>
      ))}
      <p className="text-xs text-gray-400 mb-4">Nessun pagamento e nessun contratto: invii una sola richiesta di consulenza gratuita.</p>
      <div className="rounded-2xl border-2 p-4 mb-4" style={{ borderColor: TEAL }}>
        <p className="text-sm font-semibold mb-3" style={{ fontFamily: 'Outfit', color: NAVY }}>Vuoi aggiungere un'altra soluzione per il tuo ente?</p>
        <div className="flex gap-2">
          <button className="flex-1 rounded-lg py-2.5 text-xs font-bold uppercase border-2" style={{ borderColor: NAVY, color: NAVY }}>Sì, continua</button>
          <button className="flex-1 rounded-lg py-2.5 text-xs font-bold uppercase text-white" style={{ backgroundColor: TEAL }}>No, vai alla richiesta</button>
        </div>
      </div>
      <div className="mt-auto"><Btn primary className="w-full"><MessageCircle size={13} className="inline mr-1 -mt-0.5" />Invia la richiesta di consulenza</Btn></div>
    </aside>
  </div>
);

export default function AnteprimaRestyling() {
  const [params] = useSearchParams();
  const v = params.get('v') || 'home';
  useEffect(() => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;600&family=Work+Sans:wght@400;500;600&display=swap';
    document.head.appendChild(l);
    return () => document.head.removeChild(l);
  }, []);
  return (
    <div style={{ backgroundColor: BG, minHeight: '100vh' }} data-testid="anteprima-restyling">
      <div className="text-center text-[11px] py-1.5 text-white" style={{ backgroundColor: '#F39200', fontFamily: 'Work Sans' }}>
        ANTEPRIMA PROPOSTA GRAFICA — non pubblicata, in attesa di approvazione Assietica
      </div>
      <Header />
      {v === 'home' && <HomeView />}
      {v === 'soluzione' && <SoluzioneView />}
      {v === 'checkup' && <CheckupView />}
      {v === 'richiesta' && <RichiestaView />}
      <Footer />
    </div>
  );
}
